import {makeStyles} from "@material-ui/styles";
import {
	Button,
	CircularProgress,
} from "@material-ui/core";
import theme from "../../theme";
import ShoppingBag from "../../../public/images/icons/varukorg-thin.svg";
import {useTranslation} from "tools/i18n";
import { useContext, useState } from "react";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import parsePrices from "functions/parseSWPrices";
import { getCategoryBreadcrumb, getVariants } from "../../../functions/productOps";
import useSessionStore from '@services/stores/SessionStore';
import useCartStore from '@services/stores/CartStore';
import TrackingContext from '@components/providers/tracking/trackingContext';
import LikeButton from '@components/products/wishlist/LikeButton';

const useStyles = makeStyles(
	() => ({
		wrapper: {
			display: 'none',
			width: 'calc(100% + 30px)',
			gap: 1,
			[theme.breakpoints.down('xs')]: {
				'&.fixed': {
					display: 'flex',
					alignItems: 'center',
					position: 'sticky',
					left: 0,
					right: 0,
					bottom: 0,
					zIndex: 10,
					margin: '0 -15px',
					padding: 0,
					backgroundColor: theme.palette.primary.background,
					boxShadow: theme.boxShadow[4]
				},
				'&.sticky': {
					display: 'flex',
					alignItems: 'center',
					position: 'sticky',
					left: 0,
					right: 0,
					bottom: 0,
					width: 'calc(100% + 30px)',
					margin: '10px -15px 0px',
					padding: '15px 15px',
					backgroundColor: theme.palette.primary.background
				}
			}
		},
		checkmarks: {
			display: 'flex',
			width: '100%'
		},
		checkmark: {
			display: 'flex',
			alignItems: 'center',
			marginTop: 15,
			marginRight: 16,
			fontSize: 14,
			fontWeight: 300,
			color: '#333'
		},
		checkIcon: {
			fontSize: 20,
			marginRight: 6,
			color: '#24bb81'
		},
		purchaseButton: {
			...theme.button.purchase,
			padding: '22px 12px',
			fontSize: 12,
			color: theme.palette.primary.background,
			whiteSpace: 'nowrap',
			flexShrink: 0,
			[theme.breakpoints.up('md')]: {
				padding: '24px 8px',
				fontSize: 14,
				letterSpacing: 2
			},
			'&:hover, &:disabled': {
				backgroundColor: theme.button.purchaseHover.backgroundColor
			},
			'& @media (hover: none)': {
				backgroundColor: theme.button.purchaseHover.background
			},
			'.xmas &': {
				backgroundColor: theme.palette.primary.main
			}
		},
		purchaseButtonLabel: {},
		purchaseButtonDisabled: {
			color: `${theme.palette.primary.background} !important`
		},
		prices: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			padding: '0 20px'
		},
		price: {
			minWidth: 75,
			...theme.typography.productPrice,
			fontFamily: 'Chronicle, "Open Sans", sans-serif',
			textAlign: 'center',
			fontSize: 16,
			color: '#000',
			'&.special': {
				color: '#CE2017'
			},
			'&.old': {
				textDecoration: 'line-through',
				fontSize: 'inherit',
				fontWeight: 400,
				color: '#000000'
			}
		}
	}),
	{ name: 'StickyBuy' }
);

export default function StickyBuy({product, breadcrumbs, available}) {
	const { actions: trackingActions } = useContext(TrackingContext)
	const formatPrice = useSessionStore((state) => state.methods.formatPrice);
	const classes = useStyles();
	const { t } = useTranslation();
	const cartMethods  = useCartStore((state) => state.methods);
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [disabled, setDisabled] = useState(false);
	const prices = parsePrices(product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice);
    const [isSticky, setIsSticky] = useState(false);
    const [showPrice, setShowPrice] = useState(true)

	async function handlePurchase() {
		setDisabled(true);
		await purchase();
		setDisabled(false);
	}

	function purchaseTracking() {
		let price = product.calculatedPrices && product.calculatedPrices.length > 0 ? product.calculatedPrices[0] : product.calculatedPrice;

		trackingActions.addToCart({
			name: product.translated.name,
			id: product.productNumber,
			price: price.unitPrice,
			discount: Math.abs(prices?.discount?.amount),
			brand: product.manufacturer ? product.manufacturer.translated.name : '',
			category: getCategoryBreadcrumb(breadcrumbs),
			list: 'Product Detail Page',
			variant: getVariants(product),
			quantity: 1
		});
	}

	async function purchase(event) {
		await cartMethods.addToCart({product: product.id, quantity: 1});
		purchaseTracking();
	}

	// noinspection JSAnnotator
	return (
		<>
			<div className={[classes.wrapper, isSticky ? 'sticky' : 'fixed'].join(' ')}>
				{showPrice ?
					<div className={classes.prices}>
						{prices.isOnSale ?
							<>
								<span className={[classes.price, 'special'].join(' ')}>{formatPrice(prices.price)}</span>
								<span className={[classes.price, 'old'].join(' ')}>{formatPrice(prices.listPrice)}</span>
							</> : <span className={classes.price}>{formatPrice(prices.price)}</span>
						}
					</div> : ''}
				{available ?
					<>
						<Button className={classes.purchaseButton} onClick={handlePurchase} classes={{disabled: classes.purchaseButton, label: classes.purchaseButtonLabel, disabled: classes.purchaseButtonDisabled}} disabled={disabled}>
							{t('buy')}
							{disabled ? <CircularProgress size={14} color={'inherit'} style={{marginLeft: 8}} disableShrink/> : <ShoppingBag width={15} height={15} fill="white" alt={t('buy-short')} style={{marginLeft: 8, marginTop: -3}} /> }
						</Button>
					</>
					:
					<>
						<Button className={[classes.purchaseButton, classes.soldOut].join(' ')} classes={{disabled: classes.purchaseButton, label: classes.purchaseButtonLabel}} disabled={true}>{t('sold-out')}</Button>
					</>
					}

				<LikeButton
					show={true}
					productId={product.id}
					hasLightButtons={false}
					size={'large'}
				></LikeButton>
			</div>

            {isMobile ? <style jsx="true">
				{`
					.chatBubble {
						bottom: 75px;
						right: 15px;
					}
					
					.chatBubble .profile, .chatBubble .profile span, .chatBubble img {
						width: 45px;
						height: 45px;
					}
				`}
			</style> : ''}
		</>
	);
}
